import {Injectable, signal} from '@angular/core';
import {
    ReportingAccount,
    ReportingBuildingPeriod,
    reportingBuildingPeriodSchema,
} from './overview/account-overview.model';
import {
    composeComparators,
    filterNulls,
    keyComparator,
    mapParseItem,
    mapParseList,
} from '@brunata-angular/util';
import {BehaviorSubject, map, Observable, of, tap} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {DateTime} from 'luxon';
import {CostDocRequest, CostDocRequestAdapter} from './costs/models/cost-doc-request.model';
import {CostDoc, costDocSchema} from './costs/models/building-cost.model';
import {
    AttachmentFile,
    fileAttachmentListSchema,
    fileAttachmentSchema,
} from './costs/models/file-attachment.model';
import {FileAttachmentRequest} from './costs/models/file-attachment-request.model';
import {
    Legislation,
    LegislationResponse,
    legislationResponseSchema,
    LegislationsAdapter,
} from './costs/models/legislation.model';
import {Email, emailSchema} from './costs/models/email.model';
import {Sorting} from '@brunata-angular/ui';
import {AccountPeriod, accountPeriodSchema} from './costs/models/accountPeriod.model';
import {Address, AddressAdapter, addressSchema} from './costs/models/address.model';
import {catchError, take} from 'rxjs/operators';
import {interceptorSkipHeader} from '@shared/utils/errorInterceptor';
import {
    ReportingLocation,
    reportingLocationSchema,
} from '@reporting/consumers/report-consumers-manual/models/location.model';

export type SelectedAccount = {
    building: ReportingBuildingPeriod;
    account: ReportingAccount;
};

export type ReportingColumnFilters = {
    buildingName: string;
    buildingNo: string;
    accountHeading: string;
    periods: string[];
};

@Injectable({
    providedIn: 'root',
})
export class ReportingService {
    private baseUrl = `${environment.selfServiceUrl}/`;
    private reportingOverviewTab = new BehaviorSubject<
        'OVERVIEW' | 'OPEN' | 'NOT_OPEN' | 'REPORTED'
    >('OPEN');
    public reportingOverviewTab$ = this.reportingOverviewTab.asObservable();

    private searchString = new BehaviorSubject<string>('');
    public searchString$ = this.searchString.asObservable();
    #costDoc = signal<CostDoc | null>(null);

    readonly costDoc = this.#costDoc.asReadonly();
    private sorting = new BehaviorSubject<Sorting>({
        column: 'buildingName',
        direction: 'asc',
    });
    public sorting$ = this.sorting.asObservable();

    private selectedAccount = new BehaviorSubject<SelectedAccount | null>(null);
    public selectedAccount$ = this.selectedAccount.asObservable();

    private columnFilters = new BehaviorSubject<ReportingColumnFilters>({
        buildingName: '',
        buildingNo: '',
        accountHeading: '',
        periods: [],
    });
    public columnFilters$ = this.columnFilters.asObservable();

    constructor(
        private http: HttpClient,
        private buildingCostRequestAdapter: CostDocRequestAdapter,
        private legislationsAdapter: LegislationsAdapter,
        private addressAdapter: AddressAdapter,
    ) {}

    setTab(tab: 'OVERVIEW' | 'OPEN' | 'NOT_OPEN' | 'REPORTED'): void {
        this.reportingOverviewTab.next(tab);
    }

    setSearchString(param: string): void {
        this.searchString.next(param);
    }

    setColumnFilters(filters: ReportingColumnFilters): void {
        this.columnFilters.next(filters);
    }

    setSorting(sorting: Sorting): void {
        this.sorting.next(sorting);
    }

    getCityFromCountryAndPostalCode = (
        country: string,
        postalCode: string,
    ): Observable<string | null> => {
        const url = `${
            this.baseUrl
        }postalcodes/city?countrycode=${country.toUpperCase()}&postalcode=${postalCode}`;
        // Skip http interceptor because we don't want generic errors in frontend on 404.
        const headers = new HttpHeaders().set(interceptorSkipHeader, '');
        return this.http.get<{city: string}>(url, {withCredentials: true, headers}).pipe(
            map(it => it.city),
            catchError(() => of(null)),
        );
    };

    getReportingBuildingPeriods(): Observable<ReportingBuildingPeriod[]> {
        return this.http
            .get(`${this.baseUrl}overviewaccounts`, {withCredentials: true})
            .pipe(mapParseList(reportingBuildingPeriodSchema));
    }

    fetchActiveAccount(accountPeriodId: string): Observable<void> {
        return this.http
            .get(`${this.baseUrl}overviewaccounts/${accountPeriodId}`, {withCredentials: true})
            .pipe(
                mapParseItem(reportingBuildingPeriodSchema),
                tap(buildingPeriod => {
                    const account = buildingPeriod.accounts.find(
                        a => a.accountPeriodId === accountPeriodId,
                    );
                    if (account == null) {
                        // This cannot happen, but we need to check to make the compiler happy.
                        return;
                    }
                    this.selectedAccount.next({
                        building: buildingPeriod,
                        account: account,
                    });
                }),
                map(() => {
                    // Hide answer because we want people to subscribe to activeAccount$ instead.
                }),
            );
    }

    getCostsDocAttachments(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
    ): Observable<AttachmentFile[]> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/costdocs/attachments`;
        return this.http
            .get(url, {withCredentials: true})
            .pipe(map(data => fileAttachmentListSchema.parse(data)));
    }

    createCostDocAttachment(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
        fileAttachment: FileAttachmentRequest,
    ): Observable<AttachmentFile> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/costdocs/attachments`;
        return this.http
            .post(url, fileAttachment, {
                withCredentials: true,
            })
            .pipe(map(item => fileAttachmentSchema.parse(item)));
    }

    deleteCostsDocAttachments(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        attachmentId: string,
    ): Observable<unknown> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdocs/attachments/${attachmentId}`;
        return this.http.delete(url, {withCredentials: true});
    }

    saveCosts(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        costDocRequest: CostDocRequest,
    ): Observable<CostDoc> {
        const url = `${
            this.baseUrl
        }buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc?noCache=${DateTime.now().toMillis()}`;
        return this.http
            .put(url, this.buildingCostRequestAdapter.toApiModel(costDocRequest), {
                withCredentials: true,
            })
            .pipe(map(response => costDocSchema.parse(response)));
    }

    saveEmail(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        email: string,
    ): Observable<Email> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc/accountemail`;
        return this.http.put(url, {accountEmail: email}, {withCredentials: true}).pipe(
            map(response => emailSchema.parse(response)),
            tap(() =>
                this.#costDoc.update(costDoc => {
                    if (costDoc == null) {
                        return null;
                    }
                    return {
                        ...costDoc,
                        accountEmail: email,
                    };
                }),
            ),
        );
    }

    saveAccountPeriod(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        accountPeriodStart: DateTime,
        accountPeriodEnd: DateTime,
    ): Observable<AccountPeriod> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc/perioddates`;
        return this.http
            .put(
                url,
                {startDate: accountPeriodStart.toISO(), endDate: accountPeriodEnd.toISO()},
                {
                    withCredentials: true,
                },
            )
            .pipe(
                map(response => accountPeriodSchema.parse(response)),
                tap(response =>
                    this.#costDoc.update(costDoc => {
                        if (costDoc == null) {
                            return null;
                        }
                        return {
                            ...costDoc,
                            newPeriodStartDate: response.startDate,
                            newPeriodEndDate: response.endDate,
                        };
                    }),
                ),
            );
    }

    saveCustomerAddress(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        customerAddress: Address,
    ): Observable<Address> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc/administratorinfo`;
        return this.http
            .put(url, this.addressAdapter.toApiModel(customerAddress), {
                withCredentials: true,
            })
            .pipe(
                map(response => addressSchema.parse(response)),
                tap(() =>
                    this.#costDoc.update(costDoc => {
                        if (costDoc == null) {
                            return null;
                        }
                        return {
                            ...costDoc,
                            customerName: customerAddress.name,
                            customerAddress1: customerAddress.address1,
                            customerAddress2: customerAddress.address2,
                            customerCity: customerAddress.city,
                            customerPostalCode: customerAddress.postalCode,
                            customerCountryCode: customerAddress.countryCode,
                        };
                    }),
                ),
            );
    }

    saveBillingAddress(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        billingAddress: Address,
    ): Observable<Address> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc/billingaddress`;
        return this.http
            .put(url, this.addressAdapter.toApiModel(billingAddress), {
                withCredentials: true,
            })
            .pipe(
                map(response => addressSchema.parse(response)),
                tap(() =>
                    this.#costDoc.update(costDoc => {
                        if (costDoc == null) {
                            return null;
                        }
                        return {
                            ...costDoc,
                            billingAddressName: billingAddress.name,
                            billingAddress1: billingAddress.address1,
                            billingAddress2: billingAddress.address2,
                            billingAddressCity: billingAddress.city,
                            billingAddressPostalCode: billingAddress.postalCode,
                            billingCountryCode: billingAddress.countryCode,
                        };
                    }),
                ),
            );
    }

    approveCosts(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        receiptEmail: string | null,
    ): Observable<unknown> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/acceptCostDocReporting`;
        return this.http
            .post(
                url,
                {receiptEmail: receiptEmail},
                {
                    withCredentials: true,
                },
            )
            .pipe(map(response => response));
    }

    approveConsumerReporting(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        receiptEmail: string | null,
    ): Observable<ReportingLocation[]> {
        this.selectedAccount$.pipe(filterNulls(), take(1)).subscribe(data => {
            const updatedBuilding: ReportingBuildingPeriod = {
                ...data.building,
                accounts: data.building.accounts.map(account => ({
                    ...account,
                    consumerState:
                        account.accountPeriodId === accountPeriodId
                            ? 'VIEW'
                            : account.consumerState,
                    consumerAmountsAcceptedDate:
                        account.accountPeriodId === accountPeriodId
                            ? DateTime.now()
                            : account.consumerAmountsAcceptedDate,
                })),
            };
            const updatedAccount: ReportingAccount = data.account;
            if (updatedAccount.accountPeriodId === accountPeriodId) {
                updatedAccount.consumerAmountsAcceptedDate = DateTime.now();
                updatedAccount.consumerState = 'VIEW';
            }
            this.setActiveAccount(updatedBuilding, updatedAccount);
        });

        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/acceptConsumersReporting`;
        return this.http.post(url, {receiptEmail: receiptEmail}, {withCredentials: true}).pipe(
            mapParseList(reportingLocationSchema),
            map(locations =>
                locations.sort(
                    composeComparators([
                        keyComparator(location => location.secondSectorNo),
                        keyComparator(location => location.firstSectorNo),
                        keyComparator(location => location.locationNo),
                    ]),
                ),
            ),
        );
    }

    beginCostDocReporting(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
    ): Observable<CostDoc> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/beginCostDocReporting`;
        return this.http.post(url, 'Let me start :)', {withCredentials: true}).pipe(
            map(item => costDocSchema.parse(item)),
            tap(costDoc => this.#costDoc.set(costDoc)),
        );
    }

    saveLegislations(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        legislations: Legislation[],
    ): Observable<LegislationResponse[]> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/costdoc/legislations`;
        return this.http
            .put(url, this.legislationsAdapter.toApiModel(legislations), {
                withCredentials: true,
            })
            .pipe(
                mapParseList(legislationResponseSchema),
                tap(() =>
                    this.#costDoc.update(costDoc => {
                        if (costDoc == null) {
                            return null;
                        }
                        return {
                            ...costDoc,
                            legislations: legislations,
                        };
                    }),
                ),
            );
    }

    setActiveAccount(building: ReportingBuildingPeriod, account: ReportingAccount): void {
        this.selectedAccount.next({building, account});
    }

    resetCostSpecifications(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
    ): Observable<CostDoc> {
        const url = `${this.baseUrl}buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/costdoc/resetcostspecifications`;
        return this.http
            .put(
                url,
                {},
                {
                    withCredentials: true,
                },
            )
            .pipe(mapParseItem(costDocSchema));
    }
}
